import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface MyRouteStatus {
  /** program_id → cohort_id en el que el usuario está inscrito */
  cohortByProgram: Record<string, string>;
  /** program_id → true si ya tiene certificado emitido de esa ruta */
  certByProgram: Record<string, boolean>;
}

const EMPTY: MyRouteStatus = { cohortByProgram: {}, certByProgram: {} };

/**
 * Estado del usuario por ruta: inscripción (cohorte) y certificado emitido.
 * Sin sesión devuelve vacío, así las tarjetas públicas se comportan igual que antes.
 */
export function useMyRouteStatus() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['my-route-status', user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<MyRouteStatus> => {
      if (!user) return EMPTY;

      const [enrollRes, certRes] = await Promise.all([
        supabase
          .from('cohort_participants')
          .select('cohort_id, cohorts!inner(id, program_id, is_perpetual)')
          .eq('user_id', user.id),
        supabase
          .from('certificate_logs')
          .select('program_id')
          .eq('user_id', user.id)
          .not('program_id', 'is', null),
      ]);

      if (enrollRes.error) throw enrollRes.error;
      if (certRes.error) throw certRes.error;

      const cohortByProgram: Record<string, string> = {};
      for (const row of (enrollRes.data ?? []) as any[]) {
        const pid = row.cohorts?.program_id as string | undefined;
        if (!pid) continue;
        // Prioriza la primera inscripción encontrada por ruta
        cohortByProgram[pid] ??= row.cohort_id;
      }

      const certByProgram: Record<string, boolean> = {};
      for (const row of (certRes.data ?? []) as any[]) {
        if (row.program_id) certByProgram[row.program_id] = true;
      }

      return { cohortByProgram, certByProgram };
    },
    initialData: user ? undefined : EMPTY,
  });
}
